import { Injectable, inject } from '@angular/core';
import { environment } from '../../../environments/environment';
import { AddressFields } from '../models/address.model';
import { OrgSettingsService } from './org-settings.service';

/** Radar's own field names for one reverse-geocode match — see the live response captured
 *  while building this service (docs/implementation/canvassing-crm-phase0-findings.md's
 *  sibling phase-3 notes): `number`/`stateCode`/`postalCode`/`countryCode`, not this app's
 *  `house_number`/`region`/`postal_code`/`country`. No `unit` -- reverse geocoding a
 *  multi-unit building returns its street address, never a specific suite. */
interface RadarAddress {
  formattedAddress?: string;
  number?: string;
  street?: string;
  city?: string;
  stateCode?: string;
  postalCode?: string;
  countryCode?: string;
}

interface RadarReverseGeocodeResponse {
  meta: { code: number };
  addresses: RadarAddress[];
}

interface RadarAutocompleteResult {
  formattedAddress?: string;
  latitude?: number;
  longitude?: number;
}

interface RadarAutocompleteResponse {
  meta: { code: number };
  addresses: RadarAutocompleteResult[];
}

/** HERE Autosuggest's own field names for one suggestion -- see the live response shape
 *  captured in geocoding.service.forward-geocode.spec.ts's HERE fixtures. `resultType: 'place'`
 *  marks a POI/business result (has an `address.label` too, but represents a business, not
 *  the address itself) -- filtered out in forwardGeocodeHere below, same as Radar's `place`
 *  layer exclusion did. Query-refinement items (`resultType: 'categoryQuery'` /
 *  `'chainQuery'`) carry an `href` instead of `position`/`address` and are naturally excluded
 *  by the presence check on those two fields. */
interface HereAutosuggestItem {
  resultType?: string;
  address?: { label?: string };
  position?: { lat: number; lng: number };
}

interface HereAutosuggestResponse {
  items: HereAutosuggestItem[];
}

/** HERE Reverse Geocode's own field names for one match -- see the live response shape
 *  captured in geocoding.service.reverse-geocode.spec.ts's HERE fixtures. Unlike Radar's
 *  `stateCode`, HERE's address object here has no state/province abbreviation, only the
 *  full name (`state`) -- `region` will read e.g. "British Columbia" under HERE vs "BC"
 *  under Radar. Harmless: `region` is only ever displayed as free text or round-tripped
 *  through AddressFormComponent/localStorage, never pattern-matched against a fixed set
 *  of codes. */
interface HereReverseGeocodeItem {
  address?: {
    label?: string;
    houseNumber?: string;
    street?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    countryCode?: string;
  };
}

interface HereReverseGeocodeResponse {
  items: HereReverseGeocodeItem[];
}

export type ReverseGeocodeResult =
  | { success: true; address: Partial<AddressFields> }
  | { success: false; reason: 'no_match' | 'network' };

export interface GeocodedAddress {
  formattedAddress: string;
  lat: number;
  lng: number;
}

export type ForwardGeocodeResult =
  | { success: true; results: GeocodedAddress[] }
  | { success: false; reason: 'no_match' | 'network' };

/** HERE's `in=countryCode:` autosuggest filter wants ISO 3166-1 alpha-3, but
 *  OrgSettingsService.getSearchCountryCodes() (and the org_settings.search_country_codes
 *  column it reads) stores alpha-2 -- this app's one existing convention for country codes,
 *  inherited from when Radar (which takes alpha-2) was the only consumer. Converting here,
 *  at the HERE-specific call site, keeps that stored format unchanged rather than picking a
 *  new convention because one more provider showed up. Unrecognized codes are dropped rather
 *  than thrown on -- a typo'd org setting should degrade to "no country filter", not break search. */
const ISO_3166_ALPHA2_TO_ALPHA3: Record<string, string> = {
  AD: 'AND', AE: 'ARE', AF: 'AFG', AG: 'ATG', AI: 'AIA', AL: 'ALB', AM: 'ARM', AO: 'AGO',
  AQ: 'ATA', AR: 'ARG', AS: 'ASM', AT: 'AUT', AU: 'AUS', AW: 'ABW', AX: 'ALA', AZ: 'AZE',
  BA: 'BIH', BB: 'BRB', BD: 'BGD', BE: 'BEL', BF: 'BFA', BG: 'BGR', BH: 'BHR', BI: 'BDI',
  BJ: 'BEN', BL: 'BLM', BM: 'BMU', BN: 'BRN', BO: 'BOL', BQ: 'BES', BR: 'BRA', BS: 'BHS',
  BT: 'BTN', BV: 'BVT', BW: 'BWA', BY: 'BLR', BZ: 'BLZ', CA: 'CAN', CC: 'CCK', CD: 'COD',
  CF: 'CAF', CG: 'COG', CH: 'CHE', CI: 'CIV', CK: 'COK', CL: 'CHL', CM: 'CMR', CN: 'CHN',
  CO: 'COL', CR: 'CRI', CU: 'CUB', CV: 'CPV', CW: 'CUW', CX: 'CXR', CY: 'CYP', CZ: 'CZE',
  DE: 'DEU', DJ: 'DJI', DK: 'DNK', DM: 'DMA', DO: 'DOM', DZ: 'DZA', EC: 'ECU', EE: 'EST',
  EG: 'EGY', EH: 'ESH', ER: 'ERI', ES: 'ESP', ET: 'ETH', FI: 'FIN', FJ: 'FJI', FK: 'FLK',
  FM: 'FSM', FO: 'FRO', FR: 'FRA', GA: 'GAB', GB: 'GBR', GD: 'GRD', GE: 'GEO', GF: 'GUF',
  GG: 'GGY', GH: 'GHA', GI: 'GIB', GL: 'GRL', GM: 'GMB', GN: 'GIN', GP: 'GLP', GQ: 'GNQ',
  GR: 'GRC', GS: 'SGS', GT: 'GTM', GU: 'GUM', GW: 'GNB', GY: 'GUY', HK: 'HKG', HM: 'HMD',
  HN: 'HND', HR: 'HRV', HT: 'HTI', HU: 'HUN', ID: 'IDN', IE: 'IRL', IL: 'ISR', IM: 'IMN',
  IN: 'IND', IO: 'IOT', IQ: 'IRQ', IR: 'IRN', IS: 'ISL', IT: 'ITA', JE: 'JEY', JM: 'JAM',
  JO: 'JOR', JP: 'JPN', KE: 'KEN', KG: 'KGZ', KH: 'KHM', KI: 'KIR', KM: 'COM', KN: 'KNA',
  KP: 'PRK', KR: 'KOR', KW: 'KWT', KY: 'CYM', KZ: 'KAZ', LA: 'LAO', LB: 'LBN', LC: 'LCA',
  LI: 'LIE', LK: 'LKA', LR: 'LBR', LS: 'LSO', LT: 'LTU', LU: 'LUX', LV: 'LVA', LY: 'LBY',
  MA: 'MAR', MC: 'MCO', MD: 'MDA', ME: 'MNE', MF: 'MAF', MG: 'MDG', MH: 'MHL', MK: 'MKD',
  ML: 'MLI', MM: 'MMR', MN: 'MNG', MO: 'MAC', MP: 'MNP', MQ: 'MTQ', MR: 'MRT', MS: 'MSR',
  MT: 'MLT', MU: 'MUS', MV: 'MDV', MW: 'MWI', MX: 'MEX', MY: 'MYS', MZ: 'MOZ', NA: 'NAM',
  NC: 'NCL', NE: 'NER', NF: 'NFK', NG: 'NGA', NI: 'NIC', NL: 'NLD', NO: 'NOR', NP: 'NPL',
  NR: 'NRU', NU: 'NIU', NZ: 'NZL', OM: 'OMN', PA: 'PAN', PE: 'PER', PF: 'PYF', PG: 'PNG',
  PH: 'PHL', PK: 'PAK', PL: 'POL', PM: 'SPM', PN: 'PCN', PR: 'PRI', PS: 'PSE', PT: 'PRT',
  PW: 'PLW', PY: 'PRY', QA: 'QAT', RE: 'REU', RO: 'ROU', RS: 'SRB', RU: 'RUS', RW: 'RWA',
  SA: 'SAU', SB: 'SLB', SC: 'SYC', SD: 'SDN', SE: 'SWE', SG: 'SGP', SH: 'SHN', SI: 'SVN',
  SJ: 'SJM', SK: 'SVK', SL: 'SLE', SM: 'SMR', SN: 'SEN', SO: 'SOM', SR: 'SUR', SS: 'SSD',
  ST: 'STP', SV: 'SLV', SX: 'SXM', SY: 'SYR', SZ: 'SWZ', TC: 'TCA', TD: 'TCD', TF: 'ATF',
  TG: 'TGO', TH: 'THA', TJ: 'TJK', TK: 'TKL', TL: 'TLS', TM: 'TKM', TN: 'TUN', TO: 'TON',
  TR: 'TUR', TT: 'TTO', TV: 'TUV', TW: 'TWN', TZ: 'TZA', UA: 'UKR', UG: 'UGA', UM: 'UMI',
  US: 'USA', UY: 'URY', UZ: 'UZB', VA: 'VAT', VC: 'VCT', VE: 'VEN', VG: 'VGB', VI: 'VIR',
  VN: 'VNM', VU: 'VUT', WF: 'WLF', WS: 'WSM', YE: 'YEM', YT: 'MYT', ZA: 'ZAF', ZM: 'ZMB',
  ZW: 'ZWE',
};

/**
 * Wraps two geocoding providers, both switchable together at runtime via the org's
 * `geocoding_provider` setting (OrgSettingsService.getGeocodingProvider) -- an org picks
 * Radar or HERE and that choice applies to both reverse geocoding (pin-drop) and forward
 * geocoding/autocomplete (map search) at once. Both are safe to call directly from the
 * browser with their respective client-side keys -- same security model as the Google
 * OAuth client id already committed in environment.ts.
 */
@Injectable({
  providedIn: 'root',
})
export class GeocodingService {
  private orgSettings = inject(OrgSettingsService);

  /**
   * Never throws for "no address here" or "offline" -- only a genuine programming error
   * (a malformed key, a 4xx from a bad request) would. Callers (the map's pin-drop flow)
   * treat both failure reasons the same way: leave the address `geocode_status: 'pending'`
   * for the user to fill in by hand.
   */
  async reverseGeocode(lat: number, lng: number): Promise<ReverseGeocodeResult> {
    const provider = await this.orgSettings.getGeocodingProvider();
    return provider === 'here' ? this.reverseGeocodeHere(lat, lng) : this.reverseGeocodeRadar(lat, lng);
  }

  async forwardGeocode(
    query: string,
    nearLat?: number,
    nearLng?: number,
    countryCodes?: string[],
  ): Promise<ForwardGeocodeResult> {
    const provider = await this.orgSettings.getGeocodingProvider();
    return provider === 'here'
      ? this.forwardGeocodeHere(query, nearLat, nearLng, countryCodes)
      : this.forwardGeocodeRadar(query, nearLat, nearLng, countryCodes);
  }

  private async reverseGeocodeRadar(lat: number, lng: number): Promise<ReverseGeocodeResult> {
    const key = environment.radar?.publishableKey;
    if (!key) return { success: false, reason: 'network' };

    let response: Response;
    try {
      response = await fetch(`https://api.radar.io/v1/geocode/reverse?coordinates=${lat},${lng}`, {
        headers: { Authorization: key },
      });
    } catch {
      return { success: false, reason: 'network' }; // offline, DNS failure, CORS, etc.
    }

    if (!response.ok) return { success: false, reason: 'network' };

    const body = (await response.json()) as RadarReverseGeocodeResponse;
    const match = body.addresses?.[0];
    if (!match) return { success: false, reason: 'no_match' };

    return {
      success: true,
      address: {
        formatted_address: match.formattedAddress ?? '',
        house_number: match.number ?? '',
        street: match.street ?? '',
        city: match.city ?? '',
        region: match.stateCode ?? '',
        postal_code: match.postalCode ?? '',
        country: match.countryCode ?? '',
      },
    };
  }

  /**
   * HERE's Reverse Geocode API -- `at` (lat,lng) is required, same as Autosuggest's `at`
   * below. No `layers`/`resultType` filtering needed here (unlike forwardGeocodeHere):
   * reverse geocoding a point always returns the civic address at that point, not POI
   * suggestions, so the first item is always the right one -- same assumption
   * reverseGeocodeRadar already makes by reading `addresses[0]`.
   */
  private async reverseGeocodeHere(lat: number, lng: number): Promise<ReverseGeocodeResult> {
    const key = environment.here?.apiKey;
    if (!key) return { success: false, reason: 'network' };

    let response: Response;
    try {
      const params = new URLSearchParams({ at: `${lat},${lng}`, apiKey: key });
      response = await fetch(`https://revgeocode.search.hereapi.com/v1/revgeocode?${params}`);
    } catch {
      return { success: false, reason: 'network' };
    }

    if (!response.ok) return { success: false, reason: 'network' };

    const body = (await response.json()) as HereReverseGeocodeResponse;
    const match = body.items?.[0]?.address;
    if (!match) return { success: false, reason: 'no_match' };

    return {
      success: true,
      address: {
        formatted_address: match.label ?? '',
        house_number: match.houseNumber ?? '',
        street: match.street ?? '',
        city: match.city ?? '',
        region: match.state ?? '',
        postal_code: match.postalCode ?? '',
        country: match.countryCode ?? '',
      },
    };
  }

  /**
   * Uses Radar's *autocomplete* endpoint, not /v1/geocode/forward -- forward geocode has no
   * proximity-bias parameter at all (confirmed against Radar's current docs), so it was
   * returning matches from anywhere in the world regardless of where the map was centered.
   * Autocomplete's `near` param (lat,lng, same order as reverseGeocode's `coordinates` above)
   * is exactly this: a location bias for ranking/limiting results, and it's the same
   * "Publishable"-auth-level endpoint as the others here.
   *
   * `layers` deliberately excludes only `place` (POI/business results, which carry a
   * `placeLabel` instead of `formattedAddress` -- see the filter below) rather than narrowing
   * to just `address`. Radar's `address` layer only matches a *complete* civic address (house
   * number + street); a street name alone with no number -- e.g. someone typing a street they
   * want to look up, not a specific building -- matches nothing there even when Radar has
   * that street indexed, so `address`-only silently returned zero results for a large class of
   * legitimate searches. `street`/`locality`/`neighborhood`/`postalCode` catch those.
   *
   * Also worth knowing for whoever debugs a "why didn't my nearby street show up" report:
   * `near` is a soft ranking bias, not a hard geographic filter -- confirmed by hand against
   * the live API. A query that matches many same-named candidates worldwide (a bare street or
   * town name with no house number, e.g. "beacon avenue") can still surface a distant match
   * over a much closer one; `near` reliably wins once the query is specific enough to narrow
   * the candidate set (adding a house number or the city name resolves it correctly). This is
   * Radar's own relevance ranking, not something more query-parameter tuning fixes here.
   *
   * `countryCodes`, unlike `near`, IS a hard filter (also confirmed by hand) -- results
   * outside the given ISO 3166-1 alpha-2 codes are excluded entirely rather than just
   * ranked lower. Sourced from the org's `search_country_codes` setting (see
   * OrgSettingsService.getSearchCountryCodes) -- pass an empty array for unrestricted.
   */
  private async forwardGeocodeRadar(
    query: string,
    nearLat?: number,
    nearLng?: number,
    countryCodes?: string[],
  ): Promise<ForwardGeocodeResult> {
    const key = environment.radar?.publishableKey;
    if (!key) return { success: false, reason: 'network' };

    let response: Response;
    try {
      const params = new URLSearchParams({
        query,
        layers: 'address,street,postalCode,fine',
      });
      if (typeof nearLat === 'number' && typeof nearLng === 'number') {
        params.append('near', `${nearLat},${nearLng}`);
      }
      if (countryCodes && countryCodes.length > 0) {
        params.append('countryCode', countryCodes.join(','));
      }
      response = await fetch(`https://api.radar.io/v1/search/autocomplete?${params}`, {
        headers: { Authorization: key },
      });
    } catch {
      return { success: false, reason: 'network' };
    }

    if (!response.ok) return { success: false, reason: 'network' };

    const body = (await response.json()) as RadarAutocompleteResponse;
    const results = body.addresses ?? [];
    if (results.length === 0) return { success: false, reason: 'no_match' };

    return {
      success: true,
      results: results
        .filter(
          (r): r is Required<RadarAutocompleteResult> =>
            !!r.formattedAddress &&
            typeof r.latitude === 'number' &&
            typeof r.longitude === 'number',
        )
        .map((r) => ({
          formattedAddress: r.formattedAddress,
          lat: r.latitude,
          lng: r.longitude,
        })),
    };
  }

  /**
   * Uses HERE's Autosuggest API. HERE's `at` param (lat,lng) is the proximity bias, same
   * role as Radar's `near` above; unlike `near`, HERE documents `at` as *required* for
   * Autosuggest, so a missing center short-circuits to a network failure below rather than
   * sending a request HERE would reject.
   *
   * Filters out `resultType: 'place'` (POI/business results) for the same reason Radar's
   * `layers` param excludes its `place` layer above -- those aren't civic addresses. Items
   * missing `position` or `address.label` (HERE's query-refinement stubs, `resultType:
   * 'categoryQuery'` / `'chainQuery'`, which carry an `href` instead) are dropped by the
   * same presence check.
   *
   * `countryCodes` (alpha-2, from OrgSettingsService.getSearchCountryCodes) is converted to
   * the alpha-3 codes HERE's `in=countryCode:` filter requires -- see
   * ISO_3166_ALPHA2_TO_ALPHA3 above. Pass an empty array for unrestricted.
   */
  private async forwardGeocodeHere(
    query: string,
    nearLat?: number,
    nearLng?: number,
    countryCodes?: string[],
  ): Promise<ForwardGeocodeResult> {
    const key = environment.here?.apiKey;
    if (!key) return { success: false, reason: 'network' };
    if (typeof nearLat !== 'number' || typeof nearLng !== 'number') {
      return { success: false, reason: 'network' };
    }

    let response: Response;
    try {
      const params = new URLSearchParams({
        q: query,
        at: `${nearLat},${nearLng}`,
        apiKey: key,
      });
      if (countryCodes && countryCodes.length > 0) {
        const alpha3Codes = countryCodes
          .map((code) => ISO_3166_ALPHA2_TO_ALPHA3[code.toUpperCase()])
          .filter((code): code is string => !!code);
        if (alpha3Codes.length > 0) {
          params.append('in', `countryCode:${alpha3Codes.join(',')}`);
        }
      }
      response = await fetch(`https://autosuggest.search.hereapi.com/v1/autosuggest?${params}`);
    } catch {
      return { success: false, reason: 'network' };
    }

    if (!response.ok) return { success: false, reason: 'network' };

    const body = (await response.json()) as HereAutosuggestResponse;
    const items = body.items ?? [];
    const results = items
      .filter((item) => item.resultType !== 'place' && !!item.address?.label && !!item.position)
      .map((item) => ({
        formattedAddress: item.address!.label!,
        lat: item.position!.lat,
        lng: item.position!.lng,
      }));

    if (results.length === 0) return { success: false, reason: 'no_match' };
    return { success: true, results };
  }
}
